import type {
  PostInputMetaData,
  RetweetMetadataInput,
} from '../../graphqlTypes'
import { AttachmentTypes } from '~publish/legacy/constants'
import { canDiscloseAiContent } from '~publish/legacy/composer/composer/components/channel-fields/components/aiDisclosure'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import type { ChannelVariant } from '../../../store/postSlice/types'
import { mapChannelVariantThread } from '../../channelVariantToCreatePostInput'
import { mapThread } from '../../mapThread'
import type { MapperContext } from '../../types'
import { clearable } from '../shared/clearable'
import type { ServiceMapperEntry } from '../types'

// A top-level retweet attachment → `RetweetMetadataInput` (tweetId can be a
// number, `id` is a String). Omitted unless the retweet attachment is enabled.
// Per-thread-entry retweets have no slot in `ThreadedPostInput` and are dropped.
function mapRetweet(draft: Draft): RetweetMetadataInput | undefined {
  if (
    draft.enabledAttachmentType !== AttachmentTypes.RETWEET ||
    draft.retweet == null
  ) {
    return undefined
  }

  // The quote comment is the live post text (matches legacy getFormattedAPIData);
  // the stored `retweet.comment` is only populated when importing a saved quote.
  // No text → a bare native retweet (no comment).
  return {
    id: String(draft.retweet.tweetId),
    ...(draft.text ? { comment: draft.text } : {}),
  }
}

function getVariantRetweet(
  variant: ChannelVariant,
): RetweetMetadataInput | undefined {
  if (
    variant.enabledAttachmentType !== AttachmentTypes.RETWEET ||
    variant.retweet == null
  ) {
    return undefined
  }
  return {
    id: String(variant.retweet.tweetId),
    ...(variant.text ? { comment: variant.text } : {}),
  }
}

// Twitter: AI-disclosure flag (`channelData.twitter.is_ai_generated`), a
// top-level retweet, and the thread. The retweet and thread are tri-stated via
// `clearable` (omitted on create, `null` on edit to clear them).
// `isAiGenerated` is omit-when-absent, because the edit mapper ignores `null`.
//
// The composer hides the AI-disclosure field for a retweet, but the draft
// keeps the stored flag. The backend rejects that flag, so the disclosure
// gate applies regardless of source.
//
// `ctx.channelVariant` (when present) supplies all three fields. The
// disclosure gate (`!isReminder && !retweet`) is reimplemented locally
// against the variant, since the shared `canDiscloseAiContent` only accepts a
// `Draft`.
export function mapTwitter(
  draft: Draft,
  ctx: MapperContext,
): PostInputMetaData {
  const variant = ctx.channelVariant
  const canDiscloseAi = variant
    ? !variant.isReminder && !variant.retweet
    : canDiscloseAiContent(draft)
  const isAiGenerated = canDiscloseAi
    ? variant
      ? variant.channelData?.twitter?.is_ai_generated
      : draft.channelData?.twitter?.is_ai_generated
    : undefined
  const retweet = variant ? getVariantRetweet(variant) : mapRetweet(draft)
  const thread = variant
    ? mapChannelVariantThread(variant.threadItems, 'twitter')
    : mapThread(draft)

  return {
    twitter: {
      ...(isAiGenerated != null ? { isAiGenerated } : {}),
      retweet: clearable(retweet, ctx),
      thread: clearable(thread, ctx),
    },
  }
}

export const twitter: ServiceMapperEntry = {
  mapper: mapTwitter,
}
